// Outbox fuer Sichtungen, die ohne Netz entstanden sind (Projektplan 10.1).
//
// "Konflikte sind unkritisch": ein Kennzeichen, das schon in der Sammlung
// steht, ist beim Nachsenden ein 409, kein Fehler - der Server hat es
// entweder beim ersten (nie bestaetigten) Versuch schon angenommen, oder
// ein anderes Geraet war schneller. In beiden Faellen ist die Outbox-Zeile
// erledigt.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiFehler, api } from "./api.js";
import { db, type AusstehendeSichtung } from "./db.js";

export const OUTBOX_SCHLUESSEL = ["outbox-groesse"] as const;

export async function outboxEintragen(eintrag: AusstehendeSichtung): Promise<void> {
  await db.outbox.put(eintrag);
}

/** Versucht jede wartende Sichtung nachzusenden. Gibt zurueck, wie viele danach noch offen sind. */
export async function outboxSynchronisieren(): Promise<number> {
  const wartend = await db.outbox.toArray();
  for (const eintrag of wartend) {
    try {
      await api(`/collections/${eintrag.collectionId}/entries`, {
        method: "POST",
        body: { plateCodeId: eintrag.plateCodeId, clientUuid: eintrag.clientUuid },
      });
      await db.outbox.delete(eintrag.clientUuid);
    } catch (fehler) {
      // 409 bereits-gesammelt zaehlt als erledigt (siehe Kommentar oben).
      // Jeder andere Fehler (kein Netz, Server down) bleibt in der Outbox
      // fuer den naechsten Versuch.
      if (fehler instanceof ApiFehler && fehler.problem.status === 409) {
        await db.outbox.delete(eintrag.clientUuid);
      }
    }
  }
  return db.outbox.count();
}

export function useOutboxGroesse() {
  return useQuery({
    queryKey: OUTBOX_SCHLUESSEL,
    queryFn: () => db.outbox.count(),
    // Kein eigenes Polling: wer die Groesse aendert (entries.ts, dieses
    // Modul), invalidiert danach selbst.
    staleTime: Infinity,
  });
}

export function useOutboxSynchronisieren() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: outboxSynchronisieren,
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: OUTBOX_SCHLUESSEL });
      // Eintraege koennten von einem anderen Geraet oder Mitglied
      // dazugekommen sein, waehrend die Verbindung fehlte.
      void queryClient.invalidateQueries({ queryKey: ["entries"] });
    },
  });
}
